/**
 * Shared CSV export hook for popup pages
 */
import { useCallback } from 'react';

export interface CsvExportOptions<T> {
  /** Data to export */
  data: T[] | null | undefined;
  /** Column headers to include in CSV */
  headers: (keyof T)[];
  /** Optional filename prefix (defaults to 'export') */
  filenamePrefix?: string;
  /** Optional callback after export */
  onExport?: () => void;
}

/**
 * Format a value for CSV output
 * Handles null/undefined, dates, and escapes commas/quotes
 */
function formatCsvValue(v: unknown): string {
  if (v == null) return '';
  if (v instanceof Date) return v.toISOString();
  const s = String(v);
  return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Hook for exporting data to CSV
 */
export function usePopupCsvExport<T extends Record<string, unknown>>({
  data,
  headers,
  filenamePrefix = 'export',
  onExport,
}: CsvExportOptions<T>) {
  const exportToCsv = useCallback(() => {
    if (!data?.length) return;

    const csv = [
      headers.join(','),
      ...data.map((row) =>
        headers.map((h) => formatCsvValue(row[h])).join(','),
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    onExport?.();
  }, [data, headers, filenamePrefix, onExport]);

  return {
    exportToCsv,
    canExport: Boolean(data?.length),
  };
}

/**
 * Standalone CSV export function for direct use
 */
export function exportDataToCsv<T extends Record<string, unknown>>(
  data: T[],
  headers: (keyof T)[],
  filenamePrefix: string = 'export',
): void {
  if (!data?.length) return;

  const csv = [
    headers.join(','),
    ...data.map((row) => headers.map((h) => formatCsvValue(row[h])).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
